/**
 * ChatResponseStudyList component for the Indigo design system.
 *
 * Displays a header followed by a list of study / record rows. Each row
 * contains a leading icon tile, a title with an optional severity {@link Tag},
 * a description ReactNode (which may embed inline links), and an optional
 * trailing date.
 *
 * @example
 * ```tsx
 * <ChatResponseStudyList
 *   header="Imaging studies"
 *   studies={[
 *     {
 *       id: "1",
 *       icon: <Icon name="file" size="m" />,
 *       title: "MRI lumbar spine",
 *       severity: { label: "Abnormal", status: "error" },
 *       description: <>Mild disc bulge at L4–L5.</>,
 *       date: "Mar 12, 2024",
 *     },
 *   ]}
 * />
 * ```
 */
import { clsx } from "clsx";
import { forwardRef, ReactNode } from "react";
import { Body } from "../../Typography/Body";
import "./ChatResponseStudyList.css";

export type ChatResponseStudySeverity =
  | "success"
  | "warning"
  | "error"
  | "neutral"
  | "info";

export interface ChatResponseStudySeverityTag {
  /** Visible label for the tag. */
  label: string;
  /** Color status for the tag. */
  status: ChatResponseStudySeverity;
}

export interface ChatResponseStudyItem {
  /** Stable identifier used as React key. */
  id: string;
  /** Leading icon node rendered inside a 32×32 gray tile. */
  icon: ReactNode;
  /** Title text. Rendered as Body S, weight medium. */
  title: string;
  /** Optional severity tag rendered after the title. */
  severity?: ChatResponseStudySeverityTag;
  /**
   * Description content. Accepts a ReactNode so callers can embed inline
   * links / pills inside the body copy.
   */
  description?: ReactNode;
  /** Optional date / metadata rendered on the right side of the row. */
  date?: string;
  /** Click handler. When provided the row becomes interactive. */
  onClick?: () => void;
  /** Optional test id for the row. */
  "data-testid"?: string;
}

export interface ChatResponseStudyListProps {
  /** Optional header rendered above the study list. */
  header?: string;
  /** List of studies. */
  studies: ChatResponseStudyItem[];
  /** Additional CSS class applied to the root. */
  className?: string;
  /** Test id for the root element. */
  "data-testid"?: string;
}

export const ChatResponseStudyList = forwardRef<
  HTMLDivElement,
  ChatResponseStudyListProps
>(({ header, studies, className, "data-testid": dataTestId }, ref) => {
  return (
    <div
      ref={ref}
      className={clsx("indigo-chat-response-study-list", className)}
      data-testid={dataTestId}
    >
      {header && (
        <Body
          as="span"
          size="xs"
          className="indigo-chat-response-study-list__header"
        >
          {header}
        </Body>
      )}
      <div className="indigo-chat-response-study-list__list" role="list">
        {studies.map((study) => {
          const isInteractive = Boolean(study.onClick);
          const Component = isInteractive ? "button" : "div";

          return (
            <Component
              key={study.id}
              type={isInteractive ? "button" : undefined}
              role="listitem"
              onClick={study.onClick}
              className={clsx(
                "indigo-chat-response-study-list__row",
                isInteractive && "indigo-chat-response-study-list__row--button",
              )}
              data-testid={study["data-testid"]}
            >
              <span className="indigo-chat-response-study-list__icon-wrap">
                {study.icon}
              </span>
              <span className="indigo-chat-response-study-list__body">
                <span className="indigo-chat-response-study-list__title-row">
                  <Body
                    as="span"
                    size="sm"
                    className="indigo-chat-response-study-list__title"
                  >
                    {study.title}
                  </Body>
                  {study.severity && (
                    <span
                      className={clsx(
                        "indigo-chat-response-study-list__severity",
                        `indigo-chat-response-study-list__severity--${study.severity.status}`,
                      )}
                    >
                      {study.severity.label}
                    </span>
                  )}
                </span>
                {study.description && (
                  <Body
                    as="span"
                    size="xs"
                    className="indigo-chat-response-study-list__description"
                  >
                    {study.description}
                  </Body>
                )}
              </span>
              {study.date && (
                <Body
                  as="span"
                  size="xs"
                  className="indigo-chat-response-study-list__date"
                >
                  {study.date}
                </Body>
              )}
            </Component>
          );
        })}
      </div>
    </div>
  );
});

ChatResponseStudyList.displayName = "ChatResponseStudyList";

export default ChatResponseStudyList;
